import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Input } from "antd";
import clasnames from "classnames";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useSelector } from "react-redux";
import { useHotkeys } from "react-hotkeys-hook";
import { message } from "../../../components/Message";
import errorCode from "../../../utils/errCode";
import { useWorkspace } from "../../../context/WorkspaceRegionProvider";
import ConfirmModal from "../../../components/ConfirmModal";
import Copy from "../../../image/copy.svg";
import Editor from "../../../image/editor.svg";
import { editinfo, offShelveSoft } from "../../../service/detail";
import { fetchIntlSoftDetailMerged } from "../hooks/useIntlSoftApi";
import { validateIntlContactFields } from "../../../utils/intlSoft";
import detailStyles from "../../detail/index.module.less";

const { TextArea } = Input;

const IntlDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { config } = useSelector((state) => state);
  const { canUseIntl } = useWorkspace();
  const [result, setResult] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editable, setEditable] = useState(false);
  const [appEmailRefEditable, setAppEmailRefEditable] = useState(false);
  const [contactXEditable, setContactXEditable] = useState(false);
  const [contactTelegramEditable, setContactTelegramEditable] = useState(false);
  const [contactDiscordEditable, setContactDiscordEditable] = useState(false);
  const [appRemarkRefEditable, setAppRemarkRefEditable] = useState(false);
  const [appRemarkValue, setAppRemarkValue] = useState("");

  const inputRef = useRef(null);
  const appEmailRef = useRef(null);
  const contactXRef = useRef(null);
  const contactTelegramRef = useRef(null);
  const contactDiscordRef = useRef(null);
  const appRemarkRef = useRef(null);

  const configType = config?.soft_cate?.find(
    (item) => item.id == (result?.app_cate_d ?? result?.app_cate_id)
  );
  const configSt = config?.app_status?.find(
    (item) => item.id == result?.app_status_id
  );

  const loadDetail = useCallback(() => {
    if (!canUseIntl) {
      setLoading(false);
      return Promise.resolve();
    }

    setLoading(true);
    return fetchIntlSoftDetailMerged(id)
      .then((res) => {
        setResult(res || {});
        setAppRemarkValue(res?.remark || "");
      })
      .catch((error) => {
        message.show({
          type: "error",
          content: errorCode[error?.errno] || error?.errmsg || "详情加载失败",
        });
      })
      .finally(() => setLoading(false));
  }, [id, canUseIntl]);

  const closeEditMode = useCallback(() => {
    setEditable(false);
    setAppEmailRefEditable(false);
    setContactXEditable(false);
    setContactTelegramEditable(false);
    setContactDiscordEditable(false);
    setAppRemarkRefEditable(false);
  }, []);

  const focusInput = useCallback(async () => {
    const callbackUrl = inputRef?.current?.value || result.callback_url || "";
    const appEmail = appEmailRefEditable
      ? appEmailRef?.current?.value || ""
      : result.app_email || "";
    const contactX = contactXEditable
      ? contactXRef?.current?.value || ""
      : result.contact_x || "";
    const contactTelegram = contactTelegramEditable
      ? contactTelegramRef?.current?.value || ""
      : result.contact_telegram || "";
    const contactDiscord = contactDiscordEditable
      ? contactDiscordRef?.current?.value || ""
      : result.contact_discord || "";
    const appRemark = appRemarkRefEditable
      ? appRemarkValue
      : result.remark || "";

    if (!result.app_id) {
      return;
    }

    const validation = validateIntlContactFields({
      callback_url: callbackUrl,
      app_email: appEmail,
      remark: appRemark,
    });
    if (!validation.valid) {
      message.show({ type: "error", content: validation.message });
      return;
    }

    try {
      await editinfo({
        id: Number(id),
        app_id: result.app_id,
        callback_url: callbackUrl.trim(),
        app_email: appEmail.trim(),
        remark: appRemark.trim(),
        contact_x: contactX,
        contact_telegram: contactTelegram,
        contact_discord: contactDiscord,
      });
      await loadDetail();
      closeEditMode();
    } catch (error) {
      message.show({
        type: "error",
        content: errorCode[error?.errno] || error?.errmsg || "保存失败",
      });
    }
  }, [
    appEmailRefEditable,
    appRemarkRefEditable,
    appRemarkValue,
    contactDiscordEditable,
    contactTelegramEditable,
    contactXEditable,
    closeEditMode,
    id,
    loadDetail,
    result.app_email,
    result.app_id,
    result.callback_url,
    result.contact_discord,
    result.contact_telegram,
    result.contact_x,
    result.remark,
  ]);

  useHotkeys("enter", focusInput);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (editable && inputRef?.current) {
      inputRef.current.focus();
    }
  }, [editable]);

  useEffect(() => {
    const isEditing =
      editable ||
      appEmailRefEditable ||
      contactXEditable ||
      contactTelegramEditable ||
      contactDiscordEditable ||
      appRemarkRefEditable;
    if (!isEditing) {
      return undefined;
    }

    const handleClickOutside = () => {
      focusInput();
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [
    editable,
    appEmailRefEditable,
    contactXEditable,
    contactTelegramEditable,
    contactDiscordEditable,
    appRemarkRefEditable,
    focusInput,
  ]);

  const editorClickFunc = (e, num) => {
    e.stopPropagation();
    setEditable(false);
    setAppEmailRefEditable(false);
    setContactXEditable(false);
    setContactTelegramEditable(false);
    setContactDiscordEditable(false);
    setAppRemarkRefEditable(false);

    switch (num) {
      case 3:
        setAppEmailRefEditable(true);
        break;
      case 5:
        setAppRemarkRefEditable(true);
        break;
      case 6:
        setContactXEditable(true);
        break;
      case 7:
        setContactTelegramEditable(true);
        break;
      case 8:
        setContactDiscordEditable(true);
        break;
      default:
        setEditable(true);
    }
  };

  const handleDelete = () => {
    offShelveSoft({
      id: Number(id),
      app_status_id: 5,
      app_id: result.app_id,
    }).then(() => {
      navigate("/view/home");
    });
  };

  const handleRemarkChange = (e) => {
    setAppRemarkValue(e.target.value);
  };

  if (loading) {
    return <div className={detailStyles.detail}>加载中...</div>;
  }

  return (
    <div className={detailStyles.detail}>
      <div onClick={() => navigate(-1)} className={detailStyles.back}>
        {"<返回"}
      </div>
      <div className={detailStyles.detailwrapper}>
        <div className={detailStyles.detailTop}>
          <div
            className={detailStyles.logo}
            style={{
              backgroundImage: `url(${
                result?.logo_url ||
                result?.logo_pic ||
                "https://p3.ssl.qhimg.com/t01ea0f774a67ee6d1a.png"
              })`,
            }}
          />
          <div className={detailStyles.cpname}>{result?.app_name}</div>
        </div>

        <div className={detailStyles.detailContent}>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>更新时间：</div>
            <div className={detailStyles.itemVal}>{result.mtime || "--------"}</div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>AppID：</div>
            <div className={detailStyles.itemVal}>
              {result.app_id ? (
                <CopyToClipboard
                  onCopy={() => {
                    message.show({ type: "success", content: "复制成功！" });
                  }}
                  text={result.app_id}
                >
                  <div>
                    <span className={detailStyles.copytext}>{result.app_id}</span>
                    <Copy className={detailStyles.copy} />
                  </div>
                </CopyToClipboard>
              ) : (
                "--------"
              )}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>软件类型：</div>
            <div className={detailStyles.itemVal}>
              {configType?.name || "--------"}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>AppSecret：</div>
            <div className={detailStyles.itemVal}>
              {result.app_secret ? (
                <CopyToClipboard
                  onCopy={() => {
                    message.show({ type: "success", content: "复制成功！" });
                  }}
                  text={result.app_secret}
                >
                  <div>
                    <span className={detailStyles.copytext}>{result.app_secret}</span>
                    <Copy className={detailStyles.copy} />
                  </div>
                </CopyToClipboard>
              ) : (
                "--------"
              )}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>软件版本：</div>
            <div className={detailStyles.itemVal}>
              {result.app_version || "--------"}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>回调地址：</div>
            <div
              onClick={(e) => e.stopPropagation()}
              className={detailStyles.itemVal}
            >
              {editable ? (
                <input
                  ref={inputRef}
                  defaultValue={result.callback_url}
                  className={detailStyles.editInput}
                />
              ) : (
                <>
                  <span>{result.callback_url || "------"}</span>
                  <Editor
                    onClick={(e) => editorClickFunc(e, 0)}
                    className={detailStyles.editor}
                  />
                </>
              )}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>软件状态：</div>
            <div className={detailStyles.itemVal}>
              {result.app_status_id ? configSt?.name : "--------"}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}> </div>
            <div className={detailStyles.itemVal}> </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>软件环境：</div>
            <div className={detailStyles.itemVal}>
              {result.channel == 0 ? "测试" : "正式"}
            </div>
          </div>
        </div>

        <div className={detailStyles.detailContent}>
          <Divider>用户将会通过以下方式联系您</Divider>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>邮箱：</div>
            <div
              onClick={(e) => e.stopPropagation()}
              className={detailStyles.itemVal}
            >
              {appEmailRefEditable ? (
                <input
                  ref={appEmailRef}
                  defaultValue={result.app_email || ""}
                  className={detailStyles.editInput}
                />
              ) : (
                <>
                  <span>{result.app_email || "------"}</span>
                  <Editor
                    onClick={(e) => editorClickFunc(e, 3)}
                    className={detailStyles.editor}
                  />
                </>
              )}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>X：</div>
            <div
              onClick={(e) => e.stopPropagation()}
              className={detailStyles.itemVal}
            >
              {contactXEditable ? (
                <input
                  ref={contactXRef}
                  defaultValue={result.contact_x || ""}
                  className={detailStyles.editInput}
                />
              ) : (
                <>
                  <span>{result.contact_x || "------"}</span>
                  <Editor
                    onClick={(e) => editorClickFunc(e, 6)}
                    className={detailStyles.editor}
                  />
                </>
              )}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>Telegram：</div>
            <div
              onClick={(e) => e.stopPropagation()}
              className={detailStyles.itemVal}
            >
              {contactTelegramEditable ? (
                <input
                  ref={contactTelegramRef}
                  defaultValue={result.contact_telegram || ""}
                  className={detailStyles.editInput}
                />
              ) : (
                <>
                  <span>{result.contact_telegram || "------"}</span>
                  <Editor
                    onClick={(e) => editorClickFunc(e, 7)}
                    className={detailStyles.editor}
                  />
                </>
              )}
            </div>
          </div>
          <div className={detailStyles.detailItem}>
            <div className={detailStyles.itemName}>Discord：</div>
            <div
              onClick={(e) => e.stopPropagation()}
              className={detailStyles.itemVal}
            >
              {contactDiscordEditable ? (
                <input
                  ref={contactDiscordRef}
                  defaultValue={result.contact_discord || ""}
                  className={detailStyles.editInput}
                />
              ) : (
                <>
                  <span>{result.contact_discord || "------"}</span>
                  <Editor
                    onClick={(e) => editorClickFunc(e, 8)}
                    className={detailStyles.editor}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <div className={detailStyles.detailContent}>
          <div
            className={detailStyles.detailItem}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={detailStyles.itemName}>备注：</div>
            <TextArea
              value={appRemarkValue}
              onChange={handleRemarkChange}
              onBlur={focusInput}
              ref={appRemarkRef}
              disabled={!appRemarkRefEditable}
              className={detailStyles.textAreaStyle}
              rows={4}
              maxLength={100}
              showCount
            />
            <Editor
              onClick={(e) => editorClickFunc(e, 5)}
              className={detailStyles.editor}
            />
          </div>
        </div>

        <div className={detailStyles.deleteGroup}>
          <div
            onClick={() => {
              if (result.channel == 0 || result.app_status_id != 4) {
                return;
              }
              setConfirmOpen(true);
            }}
            className={clasnames(detailStyles.delete, {
              [detailStyles.disabled]: result.app_status_id != 4,
            })}
          >
            {result.app_status_id == 1 ? "未上架" : "去下架"}
          </div>
          <div
            onClick={() => {
              if (result.channel == 0 || result.app_status_id == 2) {
                return;
              }
              navigate(`/view/register/${id}`);
            }}
            className={clasnames(detailStyles.onShelves, {
              [detailStyles.disabled]:
                result.app_status_id == 2 || result.channel == 0,
            })}
          >
            {result.app_status_id == 1 ? "去上架" : "去更新"}
          </div>
        </div>
      </div>
      <ConfirmModal
        title={result?.app_name}
        onOk={handleDelete}
        onClose={() => setConfirmOpen(false)}
        open={confirmOpen}
      />
    </div>
  );
};

export default IntlDetail;
